import { ReactElement, ReactNode, PropsWithChildren } from 'react';
import { colors, s16, s32, s4, s8 } from '@ardoq/design-tokens';
import { header2 } from '@ardoq/typography';
import styled from 'styled-components';
import { WithPopover } from '@ardoq/popovers';

type SidebarHeaderProps = {
  title: string | ReactElement;
  representation?: ReactNode;
  buttons?: ReactNode;
};

const HeaderWrapper = styled.div`
  background-color: ${colors.surfaceDefaultSubtle};
  padding: ${s32} ${s16} ${s16} ${s16};
  box-shadow: 1px 1px 4px 0px ${colors.borderSubtle};
  color: ${colors.textDefault};
  display: flex;
  flex-direction: column;
  gap: ${s4};
`;

const TitleWrapper = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: min-content 1fr;
  column-gap: ${s8};
`;

const Title = styled.h1`
  ${header2};
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ChildWrapper = styled.div`
  overflow: hidden;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-height: 40px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  gap: ${s4};
`;

const MAX_TITLE_LENGTH = 57;
const PopoverDiv = styled.div`
  min-width: fit-content;
  max-width: 550px;
`;

const Heading = ({ title, representation }: SidebarHeaderProps) => {
  if (typeof title !== 'string') return title;

  const popoverContent = title.length >= MAX_TITLE_LENGTH ? title : null;
  if (representation) {
    return (
      <TitleWrapper>
        {representation}
        <WithPopover content={() => <PopoverDiv>{popoverContent}</PopoverDiv>}>
          <Title>{title}</Title>
        </WithPopover>
      </TitleWrapper>
    );
  }
  return (
    <WithPopover content={() => <PopoverDiv>{popoverContent}</PopoverDiv>}>
      <Title>{title}</Title>
    </WithPopover>
  );
};

const SidebarHeader = ({
  title,
  representation,
  children,
  buttons,
}: PropsWithChildren<SidebarHeaderProps>) => {
  return (
    <HeaderWrapper>
      <Heading title={title} representation={representation} />
      {children ? (
        <WithPopover content={() => <PopoverDiv>{children}</PopoverDiv>}>
          <ChildWrapper>{children}</ChildWrapper>
        </WithPopover>
      ) : null}
      {buttons && <ButtonWrapper>{buttons}</ButtonWrapper>}
    </HeaderWrapper>
  );
};

export { SidebarHeader };
